import React, { useEffect, useState } from "react";
import { get } from "../api/config";
import ProjectsCRUD from "./ProjectsCRUD";
import ImageUpload from "../components/ImageUpload";
import ProjectTimeline from "../components/ProjectTimeline";

function StatTile({ label, value, icon, trend, trendValue, description }) {
  return (
    <div className="group relative bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:scale-[1.02] overflow-hidden">
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-50/20 via-blue-50/10 to-purple-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">
            {label}
          </div>
          <div className="p-3 bg-gradient-to-br from-green-100 to-green-50 rounded-xl group-hover:from-green-200 group-hover:to-green-100 transition-all duration-300 shadow-sm">
            {icon}
          </div>
        </div>

        <div className="mb-2">
          <div className="text-3xl font-bold text-gray-800 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            {value}
          </div>
          {description && (
            <div className="text-xs text-gray-500 mt-1">{description}</div>
          )}
        </div>

        {trend && (
          <div
            className={`flex items-center text-sm font-medium px-2 py-1 rounded-full ${
              trend === "up"
                ? "bg-green-100 text-green-700"
                : trend === "down"
                ? "bg-red-100 text-red-700"
                : "bg-blue-100 text-blue-700"
            }`}
          >
            <span className="mr-1 text-base">
              {trend === "up" ? "↗" : trend === "down" ? "↘" : "→"}
            </span>
            {trendValue}
          </div>
        )}
      </div>

      {/* Animated border on hover */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-green-400 via-blue-500 to-purple-500 opacity-0 group-hover:opacity-5 transition-opacity duration-500" />
    </div>
  );
}

function ProgressRing({ percentage, color = "green", size = 80 }) {
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const colorClasses = {
    green: "text-green-500",
    blue: "text-blue-500",
    purple: "text-purple-500",
    orange: "text-orange-500",
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth="4"
          fill="transparent"
          className="text-gray-200"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth="4"
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={`${colorClasses[color]} transition-all duration-1000 ease-out`}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-sm font-bold ${colorClasses[color]}`}>
          {percentage}%
        </span>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(4)].map((_, index) => (
        <div
          key={index}
          className="rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 p-6 animate-pulse"
        >
          <div className="h-4 bg-gray-300 rounded w-1/2 mb-4"></div>
          <div className="h-8 bg-gray-300 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-300 rounded w-1/2"></div>
        </div>
      ))}
    </div>
  );
}

function FundSourceCard({ source, index }) {
  const colors = ["green", "blue", "purple", "orange", "red"];
  const color = colors[index % colors.length];

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-gray-900">{source.fund_source}</h4>
          <p className="text-sm text-gray-500">
            {source.count} transaction{source.count !== 1 ? "s" : ""}
          </p>
        </div>
        <div className={`text-lg font-bold text-${color}-600`}>
          {formatCurrency(source.total_amount)}
        </div>
      </div>
    </div>
  );
}

function UtilizationCard({ project }) {
  const utilization = project.utilization_percentage || 0;
  const color =
    utilization === 0 ? "orange" : utilization < 50 ? "red" : "green";

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-900 truncate">
          {project.project_name}
        </h4>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            color === "green"
              ? "bg-green-100 text-green-800"
              : color === "red"
              ? "bg-red-100 text-red-800"
              : "bg-orange-100 text-orange-800"
          }`}
        >
          {utilization}% Utilized
        </span>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Allocated</span>
          <span className="font-medium">
            {formatCurrency(project.total_allocated)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Utilized</span>
          <span className="font-medium">
            {formatCurrency(project.total_utilized)}
          </span>
        </div>
      </div>

      <div className="mt-3">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-1000 ${
              color === "green"
                ? "bg-green-500"
                : color === "red"
                ? "bg-red-500"
                : "bg-orange-500"
            }`}
            style={{ width: `${utilization}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    async function fetchStats() {
      setLoading(true);
      setError("");
      try {
        const res = await get("public/statistics");
        setStats(res?.data || null);
      } catch (e) {
        setError("Unable to load statistics.");
        console.error("Error fetching statistics:", e);
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, []);

  const formatCurrency = (amount) => {
    if (!amount) return "₹ 0";
    const numAmount = parseFloat(amount);

    if (numAmount >= 10000000) {
      return `₹ ${(numAmount / 10000000).toFixed(1)} Cr`;
    } else if (numAmount >= 100000) {
      return `₹ ${(numAmount / 100000).toFixed(1)} L`;
    } else if (numAmount >= 1000) {
      return `₹ ${(numAmount / 1000).toFixed(1)} K`;
    }

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const statIcons = {
    total_funds: (
      <svg
        className="w-6 h-6 text-green-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
        />
      </svg>
    ),
    total_allocated: (
      <svg
        className="w-6 h-6 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
    total_utilized: (
      <svg
        className="w-6 h-6 text-purple-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
    ),
    total_remaining: (
      <svg
        className="w-6 h-6 text-orange-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  };

  const getUtilizationRate = () => {
    if (!stats?.total_allocated || parseFloat(stats.total_allocated) === 0)
      return 0;
    return (
      (parseFloat(stats.total_utilized) / parseFloat(stats.total_allocated)) *
      100
    );
  };

  const getAllocationRate = () => {
    if (!stats?.total_funds || parseFloat(stats.total_funds) === 0) return 0;
    return (
      (parseFloat(stats.total_allocated) / parseFloat(stats.total_funds)) * 100
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                Fund Management Dashboard
              </h1>
              <p className="text-gray-600 text-lg">
                Comprehensive overview of funds, allocations, and project
                utilization
              </p>
            </div>
            <div className="flex items-center space-x-4 bg-white rounded-2xl px-4 py-3 shadow-sm border">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md">
                A
              </div>
              <div>
                <p className="font-semibold text-gray-900">Admin User</p>
                <p className="text-sm text-gray-500">Administrator</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center shadow-sm">
            <div className="text-red-600 text-xl font-semibold mb-3">
              Unable to Load Data
            </div>
            <p className="text-red-500 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-8 py-3 rounded-xl hover:bg-red-700 transition-colors shadow-md font-medium"
            >
              Try Again
            </button>
          </div>
        ) : stats ? (
          <>
            {/* Main Statistics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatTile
                label="Total Funds"
                value={formatCurrency(stats.total_funds)}
                icon={statIcons.total_funds}
                trend="stable"
                trendValue="Available"
              />
              <StatTile
                label="Total Allocated"
                value={formatCurrency(stats.total_allocated)}
                icon={statIcons.total_allocated}
                trend="up"
                trendValue={`${getAllocationRate().toFixed(1)}% of total`}
                description={`${stats.allocation_count} allocation${
                  stats.allocation_count !== 1 ? "s" : ""
                }`}
              />
              <StatTile
                label="Total Utilized"
                value={formatCurrency(stats.total_utilized)}
                icon={statIcons.total_utilized}
                trend="up"
                trendValue={`${getUtilizationRate().toFixed(1)}% of allocated`}
              />
              <StatTile
                label="Remaining Funds"
                value={formatCurrency(stats.total_remaining)}
                icon={statIcons.total_remaining}
                trend="stable"
                trendValue="Available"
              />
            </div>

            {/* Detailed Analytics Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              {/* Allocation Progress */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 lg:col-span-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  Fund Allocation
                </h3>
                <div className="flex flex-col items-center">
                  <ProgressRing
                    percentage={getAllocationRate()}
                    color="blue"
                    size={120}
                  />
                  <div className="mt-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {getAllocationRate().toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-500">
                      of total funds allocated
                    </div>
                  </div>
                </div>
              </div>

              {/* Utilization Progress */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 lg:col-span-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  Fund Utilization
                </h3>
                <div className="flex flex-col items-center">
                  <ProgressRing
                    percentage={getUtilizationRate()}
                    color="green"
                    size={120}
                  />
                  <div className="mt-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {getUtilizationRate().toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-500">
                      of allocated funds utilized
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 lg:col-span-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  Quick Stats
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                    <span className="text-green-700 font-medium">
                      Allocations
                    </span>
                    <span className="text-green-900 font-bold text-lg">
                      {stats.allocation_count}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-xl">
                    <span className="text-blue-700 font-medium">
                      Fund Sources
                    </span>
                    <span className="text-blue-900 font-bold text-lg">
                      {stats.fund_sources?.length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
                    <span className="text-purple-700 font-medium">
                      Active Projects
                    </span>
                    <span className="text-purple-900 font-bold text-lg">
                      {stats.utilization_by_project?.length || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Fund Sources & Project Utilization */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Fund Sources */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Fund Sources
                </h3>
                <div className="space-y-3">
                  {stats.fund_sources?.map((source, index) => (
                    <FundSourceCard key={index} source={source} index={index} />
                  ))}
                  {(!stats.fund_sources || stats.fund_sources.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      No fund sources data available
                    </div>
                  )}
                </div>
              </div>

              {/* Project Utilization */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Project Utilization
                </h3>
                <div className="space-y-4">
                  {stats.utilization_by_project?.map((project, index) => (
                    <UtilizationCard key={index} project={project} />
                  ))}
                  {(!stats.utilization_by_project ||
                    stats.utilization_by_project.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      No project utilization data available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : null}

        {/* Management Sections */}
        <div className="space-y-8">
          <ProjectsCRUD />
          <ProjectTimeline />
          <ImageUpload />
        </div>
      </div>
    </div>
  );
}

// Helper function for currency formatting
function formatCurrency(amount) {
  if (!amount) return "₹ 0";
  const numAmount = parseFloat(amount);

  if (numAmount >= 10000000) {
    return `₹ ${(numAmount / 10000000).toFixed(1)} Cr`;
  } else if (numAmount >= 100000) {
    return `₹ ${(numAmount / 100000).toFixed(1)} L`;
  } else if (numAmount >= 1000) {
    return `₹ ${(numAmount / 1000).toFixed(1)} K`;
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(numAmount);
}

export default AdminDashboard;
